/**
 * 术语库一键导出 / 一键导入
 *
 * 纯前端实现，只组合已有接口：
 *   termlist          查询账号下全部术语库（不分页）
 *   gettermentrylist  按 termId 分页拉词条（nextPageId 游标）
 *   checktermnameexit 名称查重
 *   importterm        新建术语库并写入词条
 *
 * 导入语义：逐库尽力导入，单库失败不影响其他库，最终给出逐库报告。
 * 本文件不依赖 Vue 响应式；调用方持有的数组会被原地修改，由调用方决定何时刷新视图。
 */
import * as XLSX from 'xlsx/dist/xlsx.full.min'
import AccountServer from '@/services/AccountServer'

export interface BatchEntry {
  entryKey: string
  entryValue: string
}

export type BatchStatus = 'pending' | 'running' | 'success' | 'failed' | 'warning'

/** 导出/导入过程中一个术语库的状态行 */
export interface BatchItem {
  /** 服务端 id，导出时有 */
  id?: number
  name: string
  /** 词条数：导出时为实际拉到的条数，导入时为待导入条数 */
  count: number
  status: BatchStatus
  /** i18n key，或服务端返回的 message */
  reason?: string
  /** 原始错误码，方便排查；本地产生的错误用 LOCAL_* 常量 */
  reasonCode?: string
  entries?: BatchEntry[]
  /** 解析阶段被丢弃的行数（原文或译文为空） */
  skippedEmpty?: number
  /** 解析阶段因原文重复被合并的行数 */
  skippedDuplicate?: number
  /** 词条超长被截断的次数 */
  truncated?: number
  /** 术语库名称是否被截断到 50 字符 */
  nameTruncated?: boolean
  /** 导出时服务端记录的词条数，用于和实际拉到的条数核对 */
  expectedCount?: number
}

export interface ParsedWorkbook {
  items: BatchItem[]
  /** 是否识别到目录 sheet（一键导出生成的文件） */
  hasIndex: boolean
}

export interface RunOptions {
  /** 单库词条上限，来自 userRights.termSingleTbSize，0/null 表示不限制 */
  singleLimit?: number | null
  /** 每处理完一个库回调，用于刷新进度 */
  onProgress?: (item: BatchItem, index: number, total: number) => void
  /** 外部取消：导出在每一页、导入在每一库之间检查 */
  isCancelled?: () => boolean
  /** 导出时：术语库列表拉到后立即回调，便于 UI 先渲染出全部行 */
  onListReady?: (items: BatchItem[]) => void
  /** 导出时同时拉取的术语库个数，默认 EXPORT_CONCURRENCY；导入始终串行 */
  concurrency?: number
  /**
   * 导出时只导出这些术语库（列表页勾选项，跨页保留）。
   * 不传或为空 = 导出账号下全部。勾选里已被删除的库会记为"不存在"。
   */
  selected?: { id: number; termName?: string }[]
}

const PAGE_SIZE = 1000
/** 导出并发度：只读查询，3 路并发在明显缩短总时长和不给服务端压力之间取平衡 */
const EXPORT_CONCURRENCY = 3
const NAME_MAX = 50
const ENTRY_MAX = 50
const SHEET_NAME_MAX = 31

/** 本地错误码（不会和服务端数字码冲突） */
export const LOCAL_CANCELLED = 'LOCAL_CANCELLED'
export const LOCAL_PARSE = 'LOCAL_PARSE'
export const LOCAL_PAGING = 'LOCAL_PAGING'
export const LOCAL_NOT_FOUND = 'LOCAL_NOT_FOUND'

/**
 * 表头识别词：覆盖各语言包里 art_entry_header_title2/3 的取值，以及服务端单库导出的固定表头。
 * 只用于没有目录页的文件；一键导出生成的文件靠目录页结构判断，不依赖这里。
 */
const HEADER_SOURCE = new Set([
  '原文', 'original text', 'source', 'originaltext', 'texte source', 'teks asli', 'testo originale',
  '원문', 'вихідний текст', 'النص الأصلي', 'मूल पाठ', 'entrykey', 'key'
])
const HEADER_TARGET = new Set([
  '译文', '訳文', '譯文', 'translated text', 'target', 'übersetzung', 'traduction', 'terjemahan',
  'traduzione', '번역문', 'переклад', 'الترجمة', 'अनुवाद', 'translation', 'entryvalue', 'value'
])

/** 服务端错误码 / 本地错误码 → i18n key */
export const ERROR_I18N: Record<string, string> = {
  '10026': 'art_batch_err_name_exist',
  '10027': 'art_batch_err_single_limit',
  '10028': 'art_batch_err_total_limit',
  '10029': 'art_batch_err_term_limit',
  '403': 'art_batch_err_token',
  [LOCAL_CANCELLED]: 'art_batch_err_cancelled',
  [LOCAL_PAGING]: 'art_batch_err_paging',
  [LOCAL_NOT_FOUND]: 'art_batch_err_not_found',
}

/** 解析阶段就已确定、重试也不可能成功的失败原因 */
const PARSE_FAILURES = new Set(['art_batch_err_name_empty', 'art_batch_err_no_entries'])

function isBlank(v: any): boolean {
  return v === null || v === undefined || String(v).trim().length === 0
}

function cellText(v: any): string {
  if (v === null || v === undefined) return ''
  return String(v).trim()
}

/**
 * 按 UTF-16 单元截断（xlsx 库和现有输入框都按 .length 计数），
 * 但不把 emoji 等代理对切成一半：截断点落在高位代理上时再退一位。
 */
function truncateChars(s: string, max: number): string {
  if (s.length <= max) return s
  let end = max
  const code = s.charCodeAt(end - 1)
  if (code >= 0xd800 && code <= 0xdbff) end--
  return s.slice(0, end)
}

function withCode(message: string, code?: string) {
  return Object.assign(new Error(message), { code })
}

function sanitizeSheetName(name: string): string {
  // Excel 不允许 []:*?/\ ，也不允许以单引号开头或结尾
  return name.replace(/[\[\]:*?/\\]/g, '_').replace(/^'+|'+$/g, '')
}

/**
 * 生成合法且唯一的 sheet 名：`序号_名称`，总长不超过 31。
 */
function buildSheetName(index: number, name: string, used: Set<string>): string {
  const prefix = `${index}_`
  const base = sanitizeSheetName(name)
  let candidate = truncateChars(prefix + base, SHEET_NAME_MAX)
  let n = 1
  while (used.has(candidate.toLowerCase())) {
    const suffix = `~${n++}`
    candidate = truncateChars(prefix + base, SHEET_NAME_MAX - suffix.length) + suffix
  }
  used.add(candidate.toLowerCase())
  return candidate
}

// ---------------------------------------------------------------------------
// 导出
// ---------------------------------------------------------------------------

/** 拉取账号下全部术语库（服务端不分页） */
export async function fetchAllLibraries(): Promise<any[]> {
  const res = await AccountServer.getUserSearchTermlist({})
  if (!res || res.code !== '1') {
    throw withCode(res?.message || 'list failed', res?.code)
  }
  return res.list || []
}

/**
 * 按游标拉完一个术语库的全部词条，返回按 id 升序。
 * 每页之间检查取消；游标不前进或页数超出预期时中止，避免服务端行为变化导致死循环。
 */
export async function fetchAllEntries(
  termId: number,
  options: { isCancelled?: () => boolean; expectedCount?: number } = {}
): Promise<BatchEntry[]> {
  const all: any[] = []
  let nextPageId: number | undefined = undefined
  const maxPages = options.expectedCount && options.expectedCount > 0
    ? Math.ceil(options.expectedCount / PAGE_SIZE) + 5
    : 200
  for (let page = 0; ; page++) {
    if (options.isCancelled && options.isCancelled()) throw withCode('cancelled', LOCAL_CANCELLED)
    if (page >= maxPages) throw withCode('too many pages', LOCAL_PAGING)
    const param: any = { termId, pageSize: PAGE_SIZE }
    if (nextPageId !== undefined) param.nextPageId = nextPageId
    const res = await AccountServer.getTermentrylist(param)
    if (!res || res.code !== '1') {
      throw withCode(res?.message || 'entry list failed', res?.code)
    }
    const list: any[] = res.list || []
    all.push(...list)
    if (list.length < PAGE_SIZE) break
    const lastId = Number(list[list.length - 1].id)
    if (!Number.isFinite(lastId) || (nextPageId !== undefined && lastId >= nextPageId)) {
      throw withCode('cursor did not advance', LOCAL_PAGING)
    }
    nextPageId = lastId
  }
  all.reverse()
  return all.map((e) => ({ entryKey: cellText(e.entryKey), entryValue: cellText(e.entryValue) }))
}

export interface ExportLabels {
  indexSheet: string
  colIndex: string
  colName: string
  colCount: string
  colSheet: string
  colSource: string
  colTarget: string
}

/**
 * 把多个术语库写成一个 xlsx：
 *   sheet1 目录：序号 | 名称 | 词条数 | sheet名
 *   sheetN 每库一张：原文 | 译文（与服务端单库导出格式一致）
 * 表头文字跟随当前语言，导入时靠目录页结构识别并跳过表头，不依赖表头文字。
 */
export function buildWorkbook(items: BatchItem[], labels: ExportLabels) {
  const wb = XLSX.utils.book_new()
  const used = new Set<string>()
  const indexSheetName = truncateChars(sanitizeSheetName(labels.indexSheet), SHEET_NAME_MAX) || 'Index'
  used.add(indexSheetName.toLowerCase())

  const indexRows: any[][] = [[labels.colIndex, labels.colName, labels.colCount, labels.colSheet]]
  const sheets: { name: string; rows: any[][] }[] = []

  items.forEach((item, i) => {
    const sheetName = buildSheetName(i + 1, item.name, used)
    const rows: any[][] = [[labels.colSource, labels.colTarget]]
    ;(item.entries || []).forEach((e) => rows.push([e.entryKey, e.entryValue]))
    indexRows.push([i + 1, item.name, (item.entries || []).length, sheetName])
    sheets.push({ name: sheetName, rows })
  })

  const indexWs = XLSX.utils.aoa_to_sheet(indexRows)
  indexWs['!cols'] = [{ wch: 6 }, { wch: 40 }, { wch: 10 }, { wch: 34 }]
  XLSX.utils.book_append_sheet(wb, indexWs, indexSheetName)
  sheets.forEach((s) => {
    const ws = XLSX.utils.aoa_to_sheet(s.rows)
    ws['!cols'] = [{ wch: 40 }, { wch: 40 }]
    XLSX.utils.book_append_sheet(wb, ws, s.name)
  })
  return wb
}

export function downloadWorkbook(wb: any, fileName: string) {
  XLSX.writeFile(wb, fileName, { bookType: 'xlsx', compression: true })
}

export function exportFileName(prefix: string): string {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${prefix}_${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}.xlsx`
}

/** 把还没开始的项标记为指定失败原因（登录失效 / 用户取消） */
function failPending(items: BatchItem[], code: string) {
  items.forEach((it) => {
    if (it.status === 'pending') {
      it.status = 'failed'
      it.reasonCode = code
      it.reason = ERROR_I18N[code]
    }
  })
}

/**
 * 一键导出主流程。逐库拉词条，失败的库记为 failed 并继续。
 * 拉取是只读操作，用固定并发度的 worker 池并行；结果顺序与列表顺序一致。
 * 取消后：正在拉的库在下一页中止并记为"已取消"，未开始的库全部记为"已取消"。
 * 返回值即最终报告；调用方决定是否下载。
 */
export async function runExport(options: RunOptions = {}): Promise<BatchItem[]> {
  const all = await fetchAllLibraries()
  const toItem = (lib: any): BatchItem => ({
    id: lib.id,
    name: cellText(lib.termName),
    count: 0,
    status: 'pending',
    entries: [],
    expectedCount: typeof lib.entryNum === 'number' ? lib.entryNum : undefined,
  })
  let items: BatchItem[]
  if (options.selected && options.selected.length > 0) {
    // 只导出勾选项：按勾选顺序排列；勾选里已不存在的库直接记失败，不发请求
    const byId = new Map<number, any>(all.map((lib) => [Number(lib.id), lib]))
    items = options.selected.map((sel) => {
      const lib = byId.get(Number(sel.id))
      if (lib) return toItem(lib)
      return {
        id: sel.id,
        name: cellText(sel.termName),
        count: 0,
        status: 'failed',
        entries: [],
        reasonCode: LOCAL_NOT_FOUND,
        reason: ERROR_I18N[LOCAL_NOT_FOUND],
      }
    })
  } else {
    items = all.map(toItem)
  }
  options.onListReady && options.onListReady(items)

  const concurrency = Math.max(1, options.concurrency || EXPORT_CONCURRENCY)
  const isCancelled = () => !!(options.isCancelled && options.isCancelled())
  let next = 0
  let aborted = false

  async function exportOne(i: number) {
    const item = items[i]
    item.status = 'running'
    options.onProgress && options.onProgress(item, i, items.length)
    try {
      item.entries = await fetchAllEntries(item.id as number, { isCancelled, expectedCount: item.expectedCount })
      item.count = item.entries.length
      if (item.expectedCount !== undefined && item.expectedCount !== item.count) {
        // 服务端记录的条数与实际拉到的不一致，标黄提示但仍导出
        item.status = 'warning'
        item.reason = 'art_batch_warn_count_mismatch'
      } else {
        item.status = 'success'
      }
    } catch (e: any) {
      item.status = 'failed'
      item.entries = []
      item.reasonCode = e?.code
      item.reason = ERROR_I18N[e?.code] || e?.message || 'art_batch_err_unknown'
      if (e?.code === '403') {
        // 登录失效，后面不可能成功：停止派发，把还没开始的全部标记失败
        aborted = true
        failPending(items, '403')
      }
    }
    options.onProgress && options.onProgress(item, i, items.length)
  }

  async function worker() {
    while (!aborted) {
      if (isCancelled()) return
      const i = next++
      if (i >= items.length) return
      if (items[i].status !== 'pending') continue
      await exportOne(i)
    }
  }

  const workers = Array.from({ length: Math.min(concurrency, items.length) }, () => worker())
  await Promise.all(workers)
  if (isCancelled()) failPending(items, LOCAL_CANCELLED)
  return items
}

// ---------------------------------------------------------------------------
// 导入
// ---------------------------------------------------------------------------

function readFileAsWorkbook(file: File): Promise<any> {
  return new Promise((resolve, reject) => {
    const rd = new FileReader()
    rd.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer)
        resolve(XLSX.read(data, { type: 'array' }))
      } catch (err) {
        reject(err)
      }
    }
    rd.onerror = reject
    rd.readAsArrayBuffer(file)
  })
}

function sheetRows(ws: any): any[][] {
  if (!ws || !ws['!ref']) return []
  return XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: true }) as any[][]
}

interface IndexInfo {
  /** 目录 sheet 名 */
  indexSheet: string
  /** 目录中能对上工作表的行 */
  mapping: { name: string; sheet: string }[]
}

/**
 * 识别目录 sheet：第一张表，数据行第 4 列指向工作簿里存在的其他 sheet。
 * 与语言无关，不依赖表头文字。允许少量行对不上（用户改过 sheet 名或删了 sheet），
 * 只要有效行不少于一半就认定为目录。
 */
function detectIndex(wb: any): IndexInfo | null {
  const first = wb.SheetNames[0]
  if (!first || wb.SheetNames.length < 2) return null
  const rows = sheetRows(wb.Sheets[first])
  if (rows.length < 2) return null
  const names = new Set<string>(wb.SheetNames)
  const mapping: { name: string; sheet: string }[] = []
  let dataRows = 0
  for (let r = 1; r < rows.length; r++) {
    const row = rows[r]
    if (!row || row.every(isBlank)) continue
    dataRows++
    const sheet = cellText(row[3])
    const name = cellText(row[1])
    if (!sheet || sheet === first || !names.has(sheet)) continue
    mapping.push({ name: name || sheet, sheet })
  }
  if (mapping.length === 0 || mapping.length * 2 < dataRows) return null
  return { indexSheet: first, mapping }
}

/** 没有目录页的文件：第一行两列都是表头词才当作表头跳过，避免误删真实词条 */
function isHeaderRow(row: any[]): boolean {
  const a = cellText(row[0]).toLowerCase()
  const b = cellText(row[1]).toLowerCase()
  return HEADER_SOURCE.has(a) && HEADER_TARGET.has(b)
}

/** 把一张 sheet 解析成一个待导入术语库 */
function parseSheet(ws: any, name: string, skipFirstRow: boolean): BatchItem {
  const rows = sheetRows(ws)
  const entries: BatchEntry[] = []
  const seen = new Set<string>()
  let skippedEmpty = 0
  let skippedDuplicate = 0
  let truncated = 0

  let start = 0
  if (rows.length > 0 && (skipFirstRow || isHeaderRow(rows[0]))) start = 1

  for (let r = start; r < rows.length; r++) {
    const row = rows[r] || []
    let key = cellText(row[0])
    let value = cellText(row[1])
    if (!key || !value) {
      if (!(isBlank(row[0]) && isBlank(row[1]))) skippedEmpty++
      continue
    }
    const k2 = truncateChars(key, ENTRY_MAX)
    const v2 = truncateChars(value, ENTRY_MAX)
    if (k2 !== key) truncated++
    if (v2 !== value) truncated++
    key = k2
    value = v2
    if (seen.has(key)) { skippedDuplicate++; continue }
    seen.add(key)
    entries.push({ entryKey: key, entryValue: value })
  }

  const rawName = cellText(name)
  const libName = truncateChars(rawName, NAME_MAX)

  const item: BatchItem = {
    name: libName,
    count: entries.length,
    status: 'pending',
    entries,
    skippedEmpty,
    skippedDuplicate,
    truncated,
    nameTruncated: libName !== rawName,
  }
  if (!libName) {
    item.status = 'failed'
    item.reasonCode = LOCAL_PARSE
    item.reason = 'art_batch_err_name_empty'
  } else if (entries.length === 0) {
    item.status = 'failed'
    item.reasonCode = LOCAL_PARSE
    item.reason = 'art_batch_err_no_entries'
  }
  return item
}

/**
 * 解析上传文件。
 * 有目录 sheet 时：按目录还原真实名称并无条件跳过表头行；目录里没登记的其他 sheet 也一并导入，名称取 sheet 名。
 * 没有目录时：把每张 sheet 当作一个术语库，名称取 sheet 名，首行按表头词判断是否跳过。
 */
export async function parseWorkbook(file: File): Promise<ParsedWorkbook> {
  const wb = await readFileAsWorkbook(file)
  const index = detectIndex(wb)
  if (index) {
    const referenced = new Set(index.mapping.map((m) => m.sheet))
    const items = index.mapping.map((m) => parseSheet(wb.Sheets[m.sheet], m.name, true))
    wb.SheetNames.forEach((s: string) => {
      if (s === index.indexSheet || referenced.has(s)) return
      items.push(parseSheet(wb.Sheets[s], s, false))
    })
    return { hasIndex: true, items }
  }
  return {
    hasIndex: false,
    items: wb.SheetNames.map((s: string) => parseSheet(wb.Sheets[s], s, false)),
  }
}

/** 导入单个术语库：查重 → 新建并写入词条 */
async function importOne(item: BatchItem, singleLimit?: number | null): Promise<void> {
  if (singleLimit && item.count > singleLimit) {
    throw withCode('single limit', '10027')
  }
  const check = await AccountServer.getCheckTermName({ termName: item.name })
  if (!check || check.code !== '1') {
    throw withCode(check?.message || 'check failed', check?.code)
  }
  // 接口约定 true 表示已存在；兼容字符串 "true"
  if (String(check.data) === 'true') {
    throw withCode('name exist', '10026')
  }
  const res = await AccountServer.getTermImportterm({ termName: item.name, list: item.entries })
  if (!res || res.code !== '1') {
    throw withCode(res?.message || 'import failed', res?.code)
  }
}

/**
 * 一键导入主流程。只处理 status 为 pending 的项（"重试失败项"把可重试的失败项重置为 pending 再调用）。
 * 登录失效时把剩余项全部标记失败并退出；用户取消时把剩余项标记为已取消。
 */
export async function runImport(items: BatchItem[], options: RunOptions = {}): Promise<BatchItem[]> {
  const total = items.length
  for (let i = 0; i < total; i++) {
    if (options.isCancelled && options.isCancelled()) {
      failPending(items, LOCAL_CANCELLED)
      break
    }
    const item = items[i]
    if (item.status !== 'pending') continue
    item.status = 'running'
    item.reason = undefined
    item.reasonCode = undefined
    options.onProgress && options.onProgress(item, i, total)
    try {
      await importOne(item, options.singleLimit)
      item.status = 'success'
    } catch (e: any) {
      item.status = 'failed'
      item.reasonCode = e?.code
      item.reason = ERROR_I18N[e?.code] || e?.message || 'art_batch_err_unknown'
      if (e?.code === '403') {
        failPending(items, '403')
        break
      }
    }
    options.onProgress && options.onProgress(item, i, total)
  }
  return items
}

/** 失败项是否值得重试：解析阶段失败和登录失效不重试 */
export function isRetryable(item: BatchItem): boolean {
  if (item.status !== 'failed') return false
  if (item.reasonCode === '403' || item.reasonCode === LOCAL_PARSE) return false
  if (item.reason && PARSE_FAILURES.has(item.reason)) return false
  return true
}

export function summarize(items: BatchItem[]) {
  const s = { total: items.length, success: 0, warning: 0, failed: 0, pending: 0 }
  items.forEach((it) => {
    if (it.status === 'success') s.success++
    else if (it.status === 'warning') s.warning++
    else if (it.status === 'failed') s.failed++
    else s.pending++
  })
  return s
}
